import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ShoppingCart, Loader2 } from "lucide-react";
import { useShopify } from "../context/ShopifyContext";
import { useCart } from "../context/CartContext";

const ProductCard = ({ product, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { addToCart, isLoading } = useCart();

  const handleAddToCart = async () => {
    if (product.variantId) {
      await addToCart(product.variantId, 1);
    }
  };

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      className="product-card bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500"
      data-testid={`product-card-${product.id}`}
    >
      {/* Product Image */}
      <div className="image-zoom aspect-[4/5] bg-brand-softBeige relative overflow-hidden">
        <img
          src={product.image}
          alt={product.imageAlt || product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          onLoad={(e) => e.target.classList.add("loaded")}
          onError={(e) => {
            e.target.src = '/images/placeholder.jpg';
          }}
        />
        {!product.availableForSale && (
          <div className="absolute top-4 right-4 bg-brand-coconutBrown/80 text-white px-3 py-1 rounded-full text-xs font-body tracking-wider uppercase">
            Sold Out
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-8">
        <span className="text-brand-warmGold text-xs tracking-[0.2em] uppercase font-body block mb-2">
          Pure Intentional
        </span>
        <h3 className="font-heading text-2xl font-light text-brand-deepGreen mb-3">
          {product.name}
        </h3>
        <p className="text-brand-coconutBrown/70 text-sm font-body font-light leading-relaxed mb-6 line-clamp-3">
          {product.description || "Premium herbal hair care crafted with intention."}
        </p>

        <div className="flex items-center justify-between">
          <span className="font-heading text-xl text-brand-deepGreen">
            {product.price}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={!product.availableForSale || isLoading || !product.variantId}
            className={`btn-primary py-3 px-6 text-xs inline-flex items-center gap-2 group ${
              !product.availableForSale || !product.variantId ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ShoppingCart className="w-4 h-4" />
            )}
            {product.availableForSale ? 'Add to Cart' : 'Sold Out'}
          </button>
        </div>
      </div>
    </motion.article>
  );
};

const ProductSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden animate-pulse">
    <div className="aspect-[4/5] bg-brand-softBeige/50" />
    <div className="p-8 space-y-4">
      <div className="h-3 w-24 bg-brand-warmGold/20 rounded" />
      <div className="h-6 w-3/4 bg-brand-deepGreen/10 rounded" />
      <div className="space-y-2">
        <div className="h-3 w-full bg-brand-coconutBrown/10 rounded" />
        <div className="h-3 w-5/6 bg-brand-coconutBrown/10 rounded" />
      </div>
      <div className="flex justify-between items-center pt-4">
        <div className="h-6 w-16 bg-brand-deepGreen/10 rounded" />
        <div className="h-10 w-28 bg-brand-deepGreen/20 rounded-full" />
      </div>
    </div>
  </div>
);

const FeaturedProducts = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { filteredProducts, loading, error, selectedCategory, setSelectedCategory, categories } = useShopify();

  // Get the selected category title for display
  const selectedCategoryTitle = selectedCategory === 'all' 
    ? 'Featured Products'
    : categories.find(c => c.handle === selectedCategory)?.title || 'Products';

  return (
    <section
      ref={ref}
      id="shop"
      className="py-24 md:py-32 bg-brand-softBeige"
      data-testid="featured-products-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-brand-warmGold text-xs tracking-[0.3em] uppercase font-body block mb-4"
          >
            Our Collection
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl font-light text-brand-deepGreen mb-4"
          >
            {selectedCategoryTitle}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={isInView ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-divider mx-auto"
          />
        </div>

        {/* Category Filter Pills */}
        {!loading && categories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-5 py-2 rounded-full font-body text-sm tracking-wider transition-all duration-300 ${
                selectedCategory === 'all'
                  ? 'bg-brand-deepGreen text-white shadow-md'
                  : 'bg-white text-brand-coconutBrown/70 hover:bg-brand-deepGreen/10 hover:text-brand-deepGreen'
              }`}
            >
              All Products
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.handle)}
                className={`px-5 py-2 rounded-full font-body text-sm tracking-wider transition-all duration-300 ${
                  selectedCategory === category.handle
                    ? 'bg-brand-deepGreen text-white shadow-md'
                    : 'bg-white text-brand-coconutBrown/70 hover:bg-brand-deepGreen/10 hover:text-brand-deepGreen'
                }`}
              >
                {category.title}
              </button>
            ))}
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 max-w-6xl mx-auto">
            {[1, 2, 3].map((i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-brand-coconutBrown/70 font-body mb-4">
              Unable to load products at this time. Please try again later.
            </p>
          </motion.div>
        )}

        {/* Products Grid */}
        {!loading && !error && filteredProducts.length > 0 && (
          <div className={`grid gap-8 lg:gap-10 max-w-6xl mx-auto ${
            filteredProducts.length === 1 
              ? 'md:grid-cols-1 max-w-md' 
              : filteredProducts.length === 2 
                ? 'md:grid-cols-2 max-w-4xl' 
                : 'md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        )}

        {/* No Products in Category */}
        {!loading && !error && filteredProducts.length === 0 && selectedCategory !== 'all' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-brand-coconutBrown/70 font-body mb-4">
              No products found in this category.
            </p>
            <button
              onClick={() => setSelectedCategory('all')}
              className="btn-secondary"
            >
              View All Products
            </button>
          </motion.div>
        )}

        {/* No Products State */}
        {!loading && !error && filteredProducts.length === 0 && selectedCategory === 'all' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-brand-coconutBrown/70 font-body mb-4">
              Products coming soon. Check back later!
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProducts;
